import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createEquipment, updateEquipment, getEquipment, listEquipmentResponsibles, listEquipmentWorkPoints,
  type EquipmentListItem, type SaveEquipmentPayload,
} from '../../api/echipamente'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  manufacturer: z.string().optional(), model: z.string().optional(), serialNumber: z.string().optional(),
  manufactureYear: z.string().optional(), commissionedAt: z.string().optional(),
  location: z.string().optional(), nominalCapacity: z.string().optional(),
  status: z.enum(['Functional', 'InMentenanta', 'Defect', 'Casat']),
  responsibleUserId: z.string().optional(), costCenter: z.string().optional(),
  workPointId: z.string().min(1, 'Selectați punct de lucru'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  code: '', name: '', manufacturer: '', model: '', serialNumber: '', manufactureYear: '', commissionedAt: '',
  location: '', nominalCapacity: '', status: 'Functional', responsibleUserId: '', costCenter: '', workPointId: '',
}

const STATUSES: { value: FormValues['status']; label: string }[] = [
  { value: 'Functional', label: 'Funcțional' },
  { value: 'InMentenanta', label: 'În mentenanță' },
  { value: 'Defect', label: 'Defect' },
  { value: 'Casat', label: 'Casat' },
]

export default function EquipmentFormDialog({ equipment, onClose }: { equipment: EquipmentListItem | null | 'new'; onClose: () => void }) {
  const open = equipment !== null
  const editing = equipment !== null && equipment !== 'new'
  const qc = useQueryClient()

  const { data: responsibles } = useQuery({ queryKey: ['mentenanta', 'responsabili'], queryFn: listEquipmentResponsibles, enabled: open })
  const { data: workPoints } = useQuery({ queryKey: ['mentenanta', 'puncte-lucru'], queryFn: listEquipmentWorkPoints, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['mentenanta', 'echipament', editing ? (equipment as EquipmentListItem).id : null],
    queryFn: () => getEquipment((equipment as EquipmentListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, name: detail.name, manufacturer: detail.manufacturer ?? '', model: detail.model ?? '',
        serialNumber: detail.serialNumber ?? '', manufactureYear: detail.manufactureYear != null ? String(detail.manufactureYear) : '',
        commissionedAt: detail.commissionedAt ?? '', location: detail.location ?? '', nominalCapacity: detail.nominalCapacity ?? '',
        status: detail.status, responsibleUserId: detail.responsibleUserId ?? '', costCenter: detail.costCenter ?? '',
        workPointId: detail.workPointId,
      })
    } else if (equipment === 'new') {
      reset(EMPTY)
    }
  }, [equipment, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveEquipmentPayload = {
        code: values.code, name: values.name,
        manufacturer: values.manufacturer || null, model: values.model || null, serialNumber: values.serialNumber || null,
        manufactureYear: values.manufactureYear ? Math.trunc(Number(values.manufactureYear)) : null,
        commissionedAt: values.commissionedAt || null,
        location: values.location || null, nominalCapacity: values.nominalCapacity || null,
        status: values.status, responsibleUserId: values.responsibleUserId || null, costCenter: values.costCenter || null,
        workPointId: values.workPointId,
      }
      if (editing) return updateEquipment((equipment as EquipmentListItem).id, payload)
      return createEquipment(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mentenanta', 'echipamente'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare utilaj' : 'Utilaj nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="manufacturer" control={control} render={({ field }) => (<TextField {...field} label="Producător" fullWidth />)} />
              <Controller name="model" control={control} render={({ field }) => (<TextField {...field} label="Model" fullWidth />)} />
              <Controller name="serialNumber" control={control} render={({ field }) => (<TextField {...field} label="Nr. serie" fullWidth />)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="manufactureYear" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="An fabricație" fullWidth />
              )} />
              <Controller name="commissionedAt" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Dată punere în funcțiune" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              )} />
              <Controller name="status" control={control} render={({ field }) => (
                <TextField {...field} select label="Stare" fullWidth error={!!errors.status} helperText={errors.status?.message}>
                  {STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="location" control={control} render={({ field }) => (<TextField {...field} label="Locație (hală/zonă)" fullWidth />)} />
              <Controller name="nominalCapacity" control={control} render={({ field }) => (<TextField {...field} label="Capacitate nominală" fullWidth />)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="responsibleUserId" control={control} render={({ field }) => (
                <TextField {...field} select label="Responsabil" fullWidth>
                  <MenuItem value="">—</MenuItem>
                  {responsibles?.map((r) => <MenuItem key={r.id} value={r.id}>{r.fullName}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="costCenter" control={control} render={({ field }) => (<TextField {...field} label="Centru de cost" fullWidth />)} />
              <Controller name="workPointId" control={control} render={({ field }) => (
                <TextField {...field} select label="Punct de lucru" fullWidth error={!!errors.workPointId} helperText={errors.workPointId?.message}>
                  {workPoints?.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
